import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  previewMessageTemplate,
  resetMessageTemplate,
  updateMessageTemplate,
  type MessageTemplateCatalogEntry,
} from '../api/client';

interface ApiError {
  response?: {
    status?: number;
    data?: {
      detail?: string | { message?: string };
    };
  };
  message?: string;
}

export function messageTemplateApiError(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Request failed';
  const value: ApiError = error;
  const detail = value.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return value.message || 'Request failed';
}

function messageTemplateApiStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const value: ApiError = error;
  return value.response?.status;
}

interface MessageTemplateEditorProps {
  entry: MessageTemplateCatalogEntry;
  previewValues?: Record<string, string>;
  onChange: (entry: MessageTemplateCatalogEntry) => void;
  onSaved?: (entry: MessageTemplateCatalogEntry) => void;
}

export default function MessageTemplateEditor({
  entry,
  previewValues,
  onChange,
  onSaved,
}: MessageTemplateEditorProps) {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(entry.template);
  const [preview, setPreview] = useState('');
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [previewInvalid, setPreviewInvalid] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    setTemplate(entry.template);
  }, [entry.key, entry.template]);

  useEffect(() => {
    let active = true;
    const handle = window.setTimeout(async () => {
      setPreviewing(true);
      try {
        const response = await previewMessageTemplate(entry.key, template, previewValues);
        if (!active) return;
        setPreview(response.data.body);
        setPreviewWarnings(response.data.warnings);
        setError('');
        setPreviewInvalid(false);
      } catch (requestError) {
        if (!active) return;
        setPreview('');
        setPreviewWarnings([]);
        setError(messageTemplateApiError(requestError));
        setPreviewInvalid(messageTemplateApiStatus(requestError) === 422);
      } finally {
        if (active) setPreviewing(false);
      }
    }, 350);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [entry.key, previewValues, template]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await updateMessageTemplate(entry.key, template);
      onChange(response.data);
      onSaved?.(response.data);
      setError('');
      setPreviewInvalid(false);
    } catch (requestError) {
      setError(messageTemplateApiError(requestError));
      setPreviewInvalid(messageTemplateApiStatus(requestError) === 422);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const response = await resetMessageTemplate(entry.key);
      onChange(response.data);
      setTemplate(response.data.template);
      setError('');
      setPreviewInvalid(false);
    } catch (requestError) {
      setError(messageTemplateApiError(requestError));
    } finally {
      setResetting(false);
    }
  };

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`message-template-${entry.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-medium text-gray-900">{entry.label}</h4>
          <p className="mt-1 text-xs text-gray-500">{entry.description}</p>
        </div>
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${entry.is_customized ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
          {entry.is_customized
            ? t('settings.messageTemplates.customized', 'Customized')
            : t('settings.messageTemplates.default', 'Default')}
        </span>
      </div>

      {entry.twilio_content_managed && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {t(
            'settings.messageTemplates.twilioNote',
            'This changes free-form and fallback/session text only. Approved Twilio Content text and SIDs remain code-managed.',
          )}
        </p>
      )}

      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-medium text-gray-600">
          {t('settings.messageTemplates.template', 'Hebrew template')}
        </span>
        <textarea
          dir="rtl"
          rows={6}
          value={template}
          onChange={(event) => setTemplate(event.target.value)}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:ring-1 ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`}
        />
      </label>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {entry.allowed_placeholders.map((placeholder) => (
          <button
            key={placeholder}
            type="button"
            onClick={() => setTemplate((current) => `${current}{${placeholder}}`)}
            className={`rounded-full border px-2 py-1 font-mono text-[11px] ${entry.required_placeholders.includes(placeholder) ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-gray-200 bg-gray-50 text-gray-600'}`}
            title={t('settings.messageTemplates.insertPlaceholder', 'Insert placeholder')}
          >
            {`{${placeholder}}`}
          </button>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-gray-400">
        {t('settings.messageTemplates.requiredHint', 'Highlighted placeholders are required.')}
      </p>

      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}

      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
        <span className="block text-xs font-medium uppercase tracking-wide text-gray-400">
          {previewing
            ? t('settings.messageTemplates.previewing', 'Updating preview…')
            : previewValues
              ? t('settings.messageTemplates.currentPreview', 'Current message preview')
              : t('settings.messageTemplates.preview', 'Sample preview')}
        </span>
        <p dir="rtl" className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
          {preview || t('settings.messageTemplates.previewUnavailable', 'Preview unavailable')}
        </p>
        {previewWarnings.map((warning) => (
          <p key={warning} className="mt-2 text-xs text-amber-700">{warning}</p>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || previewInvalid || template === entry.template}
          className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? t('common.loading') : t('common.save')}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={resetting || !entry.is_customized}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {resetting
            ? t('common.loading')
            : t('settings.messageTemplates.reset', 'Reset to default')}
        </button>
      </div>
    </article>
  );
}
